import type { StravaStatus } from '@painpal/contracts';
import { formatInt } from './format';
import { plural } from './plural';

export type Backfill = NonNullable<StravaStatus['backfill']>;
export const STRAVA_DAILY_BUDGET = 900;
export const CONNECT_WATCH_MS = 90_000;
const CONNECT_KEY = 'painpal.stravaConnectAt';

const CONNECT_PENDING = 'pending';

// Noted just before the connect redirect leaves, in this tab's session storage, so the app it returns to knows a grant
// may have just changed. The return is a fresh page load, which starts the window, so time on Strava's consent page
// doesn't use it up.
export function noteStravaConnect(): void {
  sessionStorage.setItem(CONNECT_KEY, CONNECT_PENDING);
}

export function startStravaReturnWindow(now = Date.now()): void {
  if (sessionStorage.getItem(CONNECT_KEY) === CONNECT_PENDING) sessionStorage.setItem(CONNECT_KEY, String(now));
}

export function connectedRecently(now = Date.now()): boolean {
  return now - Number(sessionStorage.getItem(CONNECT_KEY)) < CONNECT_WATCH_MS;
}

// Rides keep landing after the listing ends. An API from before `importing` (mid-deploy) only knows the listing runs.
export function isImporting(backfill: Backfill | null | undefined): boolean {
  return backfill?.importing ?? backfill?.status === 'running';
}

export function importedCount(backfill: Backfill): number {
  return backfill.total === null ? backfill.done : Math.min(backfill.done, backfill.total);
}

export function importCounts(backfill: Backfill, separator = ' / '): string {
  return `${formatInt(importedCount(backfill))}${separator}${backfill.total === null ? '?' : formatInt(backfill.total)}`;
}

export function importDaysLeft(backfill: Backfill): number | null {
  if (backfill.total === null) return null;
  const remaining = Math.max(0, backfill.total - backfill.done);
  return Math.ceil(remaining / STRAVA_DAILY_BUDGET);
}

export function importProgress(backfill: Backfill): number | null {
  if (backfill.total === null || backfill.total === 0) return null;
  return Math.min(1, backfill.done / backfill.total);
}

export function importSummary(backfill: Backfill | null): string {
  if (backfill === null || backfill.status === 'idle') return 'NOT STARTED';
  if (backfill.status === 'done') return `COMPLETE · ${formatInt(backfill.done)} ${plural(backfill.done, 'RIDE')}`;
  const counts = importCounts(backfill);
  const days = importDaysLeft(backfill);
  if (days === null) return counts;
  return `${counts} · ~${days} ${plural(days, 'DAY')} LEFT`;
}
